import React, { useState, useEffect } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form } from 'antd';
import { useGetAuthTokenMutation } from 'store/authentication/authentication.actions';
import { LoginFormValues } from 'types';
import WebWorker from 'utils/web-worker';
import authWorker from 'utils/auth-worker';
import LoginForm from './Form';
import { AuthLayoutHeader } from './AuthLayoutHeader/AuthLayoutHeader';
import {
    SAuthLayoutFooterWrapper,
    SAuthLayoutHeaderWrapper, SLayout,
    SLayoutInner,
    SLayoutContent
} from '../../components/layout/AuthLayout/AuthLayout.styles';
import { Col, Row } from 'antd';
import { AuthLayoutFooter } from '../../components/layout/AuthLayout/AuthLayoutFooter/AuthLayoutFooter';
import { setUsername } from 'store/forgotPassword/forgotPassword.slice';
import { useStartPasswordResetMutation } from 'store/forgotPassword/forgotPassword.api';
import { useRequestNewOtpSeedMutation } from 'store/deviceVerification/deviceVerification.api';
import { getFingerPrint } from 'utils/webID';
import { ROUTES } from 'constants/routes';
import { useAppSelector } from '../../store';
import { getLastUsername } from '../../utils/cookies';
import { LocalStorage } from 'services';
import { getClientDateTime } from '../../utils/common';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [requestToken] = useGetAuthTokenMutation();
    const [startPasswordReset] = useStartPasswordResetMutation();
    const [requestNewOtpSeed] = useRequestNewOtpSeedMutation();
    const [error, setError] = useState('');
    const [loginForm] = Form.useForm();
    
    const { lastUsername } = useAppSelector((state) => state.auth);

    const worker = new WebWorker(authWorker);

    const usernameVal = Form.useWatch('username', loginForm);

    useEffect(() => {
        const usernameFromStore = lastUsername;
        const usernameFromStorage = getLastUsername();
        
        const validSources = [usernameFromStore, usernameFromStorage]
            .filter(username => username && username.trim() !== '' && username !== 'undefined');
        
        const initialUsername = validSources[0];
        
        if (initialUsername) {
            loginForm.setFieldsValue({ username: initialUsername });
        }
    }, [lastUsername, loginForm]);

    const onFinish = async (values: LoginFormValues) => {
        try {
            const savedDeviceId = LocalStorage.get('DEVICE_ID');
            const lastSavedUsername = getLastUsername();
            const isSameUser = lastSavedUsername && lastSavedUsername === values.username;
 
            const deviceIdToUse = isSameUser ? savedDeviceId : undefined;
            if (!isSameUser && savedDeviceId) {
                LocalStorage.remove('DEVICE_ID');
            }

            const fingerprint = getFingerPrint(true) as string;
            
            const loginData = {
                ...values,
                fingerprint,
                deviceId: deviceIdToUse,
                deviceName: deviceIdToUse ? 'Browser' : undefined,
                clientDateTime: getClientDateTime(),
            };
            
            const { token } = await requestToken(loginData).unwrap();
            if (token) {
                worker.postMessage({ action: 'SET', token });
                navigate('/', { replace: true });
            }
        } catch (err: any) {
            const savedDeviceId = LocalStorage.get('DEVICE_ID');
            const lastSavedUsername = getLastUsername();
            const isSameUser = lastSavedUsername && lastSavedUsername === values.username;
            const deviceIdWasUsed = isSameUser && savedDeviceId;

            const isDeviceVerificationRequired = 
                err?.data?.Code === 1064 ||
                err?.data?.Error?.toLowerCase()?.includes('device') ||
                err?.data?.Error?.toLowerCase()?.includes('verification') ||
                !deviceIdWasUsed;
            
            if (isDeviceVerificationRequired) {
                try {
                    const fingerprint = getFingerPrint(true) as string;
                    const otpSeedResponse = await requestNewOtpSeed({
                        username: values.username,
                        password: values.password,
                        fingerprint,
                        deviceType: 'Browser',
                        name: navigator.platform || 'Unknown',
                        manufacture: 'Browser',
                        os: navigator.platform || 'Unknown',
                        version: navigator.appVersion || 'Unknown',
                        userAgent: navigator.userAgent,
                        appVersion: 'web',
                        clientDateTime: getClientDateTime(),
                    }).unwrap();
                    
                    LocalStorage.set('LOGIN_OTP_FLOW', {
                        ...otpSeedResponse,
                        temporaryToken: null
                    }, true);
                    
                    navigate('/verify-device-warning', { replace: true });
                } catch (otpError: any) {
                    if (otpError?.data?.Error) {
                        setError(otpError.data.Error);
                    } else {
                        setError('Failed to initialize device verification');
                    }
                }
            } else {
                if (err?.data?.Error) {
                    setError(err.data.Error);
                } else {
                    setError('Log in failed due to unknown error');
                }
            }
        }
    };

    const handleForgotPasswordClick = async () => {
        if (!usernameVal || usernameVal.trim() === '') {
            setError('Please enter your email address first');
            return;
        }
        
        try {
            setError('');
            
            const fingerprint = getFingerPrint(true) as string;
            dispatch(setUsername(usernameVal));

            await startPasswordReset({
                username: usernameVal,
                fingerprint,
                clientDateTime: getClientDateTime(),
            }).unwrap();

            navigate(generatePath(ROUTES.forgot.path, { type: 'forgot-password' }));
        } catch (err: any) {
            if (err?.data?.Error) {
                setError(err.data.Error);
            } else {
                setError('Failed to initiate password reset');
            }
        }
    };

    return (
        <SLayout>
            <SAuthLayoutHeaderWrapper>
                <AuthLayoutHeader/>
            </SAuthLayoutHeaderWrapper>
            <SLayoutInner>
                <Row justify={'center'} style={{width: '100%'}}>
                    <Col style={{ display:'flex', flexDirection:'column' }} xs={{ span: 22 }} md={{ span: 12 }} lg={{ span: 12 }}>
                        <SLayoutContent>
                            <LoginForm 
                                form={loginForm} 
                                onFinish={onFinish} 
                                error={error} 
                                onForgotPasswordClick={handleForgotPasswordClick}
                            />
                        </SLayoutContent>
                        <SAuthLayoutFooterWrapper>
                            <AuthLayoutFooter/>
                        </SAuthLayoutFooterWrapper>
                    </Col>
                </Row>
            </SLayoutInner>
        </SLayout>
    );
};

export default LoginPage;
